import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";
import QRCode from "qrcode";
import jsPDF from "jspdf";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const COMPANY_INFO = {
  logo: "/assets/img/secureLogo.png", 
  
};

const TagList = () => {
  const navigate = useNavigate();
  
  const [tags, setTags] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [sites, setSites] = useState([]);
  
  const [showAffectModal, setShowAffectModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [modalAction, setModalAction] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  // États pour les erreurs d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États pour la génération des QR Codes
  const [selectedTagsForQR, setSelectedTagsForQR] = useState([]);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [qrCodes, setQrCodes] = useState([]);
  const [qrGenerationStatus, setQrGenerationStatus] = useState({});
  const [showQRPreview, setShowQRPreview] = useState(false);
  const [qrImages, setQrImages] = useState({});
  const [logoBase64, setLogoBase64] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [exportingList, setExportingList] = useState(false);

  // Fonction pour réinitialiser les erreurs d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  // Fonction pour gérer les erreurs d'abonnement
  const handleAbonnementError = (error) => {
    if (error.response?.data?.code === 'ABONNEMENT_REQUIS') {
      setAbonnementError(true);
      setAbonnementMessage(error.response.data.message || "Abonnement inactif");
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchData();
    fetchCompanyInfo();
  }, []);

  // Récupérer les informations de la société depuis l'API
  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/societe/info.php`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success && response.data.data) {
        const societe = response.data.data;
        setCompanyData(societe);
        
        // Mettre à jour les informations de la société avec les données de l'API
        COMPANY_INFO.name = societe.nom_societe || "SecureControle";
        COMPANY_INFO.address = societe.address_societe || "Adresse non spécifiée";
        COMPANY_INFO.phone = societe.tel_societe ? `+227 ${societe.tel_societe}` : "+227 91 15 66 95";
        COMPANY_INFO.email = societe.email_societe || "contact@securecontrole.com";
        
        // Si la société a un logo stocké dans la base de données, utiliser celui-là
        if (societe.logo_path && societe.logo_path.trim() !== "") {
          COMPANY_INFO.logo = `${API_BASE_URL}/${societe.logo_path}`;
        } else {
          // Sinon utiliser le logo par défaut
          COMPANY_INFO.logo = "/assets/img/secureLogo.png";
        }
        
        loadLogo();
      }
    } catch (error) {
      console.warn("Impossible de charger les informations de la société:", error);
      loadLogo(); // Charger quand même le logo par défaut
    }
  };

  // Charger le logo en base64
  const loadLogo = async () => {
    try {
      // Essayer de charger le logo depuis le serveur
      if (COMPANY_INFO.logo) {
        try {
          const logoUrl = COMPANY_INFO.logo.startsWith('http') 
            ? COMPANY_INFO.logo 
            : `${window.location.origin}${COMPANY_INFO.logo}`;
          
          const response = await fetch(logoUrl);
          if (response.ok) {
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoBase64(reader.result);
            };
            reader.readAsDataURL(blob);
          } else {
            console.warn("Logo non trouvé, création du logo de secours");
            createFallbackLogo();
          }
        } catch (error) {
          console.warn("Erreur lors du chargement du logo:", error);
          createFallbackLogo();
        }
      } else {
        // Créer un logo de secours
        createFallbackLogo();
      }
    } catch (error) {
      console.warn("Erreur générale lors du chargement du logo:", error);
      createFallbackLogo();
    }
  };

  // Créer un logo de secours
  const createFallbackLogo = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 60;
      const ctx = canvas.getContext('2d');
      
      // Fond gradient bleu
      const gradient = ctx.createLinearGradient(0, 0, 200, 60);
      gradient.addColorStop(0, '#0B3C5D');
      gradient.addColorStop(1, '#1E88C8');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 200, 60);
      
      // Texte avec le vrai nom de la société
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const companyName = COMPANY_INFO.name || "SecureControle";
      if (companyName.length > 12) {
        ctx.font = 'bold 18px Arial';
      }
      ctx.fillText(companyName.toUpperCase(), 100, 30);
      
      setLogoBase64(canvas.toDataURL('image/png'));
    } catch (error) {
      console.error("Erreur lors de la création du logo de secours:", error);
    }
  };

  // Appliquer les filtres
  useEffect(() => {
    if (tags.length > 0) {
      let filtered = [...tags];

      if (searchTerm.trim() !== "") {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(tag =>
          tag.info_tag && tag.info_tag.toLowerCase().includes(term)
        );
      }

      if (filterStatut) {
        if (filterStatut === "actif") {
          filtered = filtered.filter(tag => tag.statut_tag === "actif");
        } else if (filterStatut === "inactif") {
          filtered = filtered.filter(tag => tag.statut_tag !== "actif");
        }
      }

      if (filterSite) {
        filtered = filtered.filter(tag =>
          tag.site_affecte && tag.site_affecte.id_site &&
          tag.site_affecte.id_site.toString() === filterSite.toString()
        );
      }

      if (filterClient) {
        filtered = filtered.filter(tag =>
          tag.site_affecte?.client?.id_client &&
          tag.site_affecte.client.id_client.toString() === filterClient.toString()
        );
      }

      setFilteredTags(filtered);
      setCurrentPage(1);

      if (filtered.length === 0 && (searchTerm || filterStatut || filterSite || filterClient)) {
        setInfoMsg("Aucun tag ne correspond aux critères de recherche.");
      } else {
        setInfoMsg("");
      }
    } else {
      setFilteredTags([]);
    }
  }, [tags, searchTerm, filterStatut, filterSite, filterClient]);

  const getAffectedTags = () => {
    return tags.filter(tag => 
      tag.statut_tag === "actif" && 
      tag.site_affecte && 
      tag.site_affecte.id_site
    );
  };

  const fetchData = async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    resetAbonnementError();
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Récupérer les tags
      const tagsResponse = await axios.get(`${API_BASE_URL}/tags/liste_tag.php`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (tagsResponse.data?.success) {
        const tagsData = tagsResponse.data.data || [];
        const formattedTags = tagsData.map(tag => ({
          ...tag,
          site_affecte: tag.site_affecte && typeof tag.site_affecte === 'object' && tag.site_affecte.id_site ? tag.site_affecte : null,
          hasExistingQR: tag.qr_code && tag.qr_code.trim() !== ""
        }));
        setTags(formattedTags);
        setFilteredTags(formattedTags);
        
        if (formattedTags.length === 0) {
          setInfoMsg("Aucun tag trouvé dans la base de données.");
        }
      } else {
        setServerError(tagsResponse.data?.message || "Impossible de charger la liste des tags.");
      }

      // Récupérer la liste des sites
      const sitesResponse = await axios.get(`${API_BASE_URL}/sites/list.php`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (sitesResponse.data?.success) {
        setSites(sitesResponse.data.data || []);
      }

    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    console.error("Fetch error:", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      if (handleAbonnementError(err)) {
        // L'erreur d'abonnement est déjà gérée
        return;
      }
      setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des données.");
    }
  };

  const handleViewDetails = (tag) => {
    navigate(`/tags/${tag.id_tag}`);
  };

  const handleCreateTag = () => {
    navigate("/tags/creer");
  };

  // Ouvrir modal pour générer QR Codes
  const openQRModal = () => {
    const affectedTags = getAffectedTags();
    if (affectedTags.length === 0) {
      setInfoMsg("Aucun tag actif affecté à un site. Affectez d'abord des tags à des sites.");
      return;
    }
    setSelectedTagsForQR([]);
    setQrCodes([]);
    setQrImages({});
    setShowQRPreview(false);
    setShowQRModal(true);
  };

  // Fermer le modal QR
  const closeQRModal = () => {
    setShowQRModal(false);
    setSelectedTagsForQR([]);
    setQrCodes([]);
    setQrImages({});
    setShowQRPreview(false);
  };

  // Sélectionner/désélectionner un tag pour QR
  const toggleTagForQR = (tagId) => {
    if (selectedTagsForQR.includes(tagId)) {
      setSelectedTagsForQR(selectedTagsForQR.filter(id => id !== tagId));
    } else {
      setSelectedTagsForQR([...selectedTagsForQR, tagId]);
    }
  };

  // Sélectionner tous les tags
  const selectAllTagsForQR = () => {
    const affectedTags = getAffectedTags();
    setSelectedTagsForQR(affectedTags.map(tag => tag.id_tag));
  };

  // Désélectionner tous les tags
  const deselectAllTagsForQR = () => {
    setSelectedTagsForQR([]);
  };

  // Générer un QR Code à partir d'un texte
  const generateQRCodeImage = async (text) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(text, {
        width: 200,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrDataUrl;
    } catch (error) {
      console.error('Erreur lors de la génération du QR Code:', error);
      return null;
    }
  };

  // Générer les QR Codes
 // Remplacer la fonction generateQRCodes dans TagList.js

const generateQRCodes = async () => {
  if (selectedTagsForQR.length === 0) {
    setInfoMsg("Veuillez sélectionner au moins un tag.");
    return;
  }

  setGeneratingQR(true);
  setQrGenerationStatus({});
  setQrCodes([]);
  setQrImages({});
  resetAbonnementError();

  try {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    // Vérifier d'abord si l'utilisateur a un abonnement actif
    // en testant avec le premier tag
    if (selectedTagsForQR.length > 0) {
      const firstTagId = selectedTagsForQR[0];
      try {
        const testResponse = await axios.post(
          `${API_BASE_URL}/tags/generate_qr.php`,
          { id_tag: firstTagId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (testErr) {
        if (testErr.response?.status === 403 && handleAbonnementError(testErr)) {
          // L'erreur d'abonnement est déjà gérée
          setGeneratingQR(false);
          setShowQRModal(false);
          return;
        }
      }
    }

    const results = [];
    const errors = [];
    const newQrImages = {};

    // Générer les QR codes un par un
    for (const tagId of selectedTagsForQR) {
      try {
        setQrGenerationStatus(prev => ({
          ...prev,
          [tagId]: { status: 'generating', message: 'Génération en cours...' }
        }));

        const tagInfo = tags.find(t => t.id_tag === tagId);
        
        // Vérifier si le tag a déjà un QR code
        if (tagInfo?.hasExistingQR) {
          // Utiliser le QR code existant
          const qrImage = await generateQRCodeImage(tagInfo.qr_code);
          
          if (qrImage) {
            newQrImages[tagId] = qrImage;
            
            results.push({
              id_tag: tagId,
              qr_code: tagInfo.qr_code,
              tag_info: tagInfo.info_tag || `Tag ${tagId}`,
              site_info: tagInfo.site_affecte || null,
              is_new: false,
              is_existing: true
            });
            
            setQrGenerationStatus(prev => ({
              ...prev,
              [tagId]: { status: 'success', message: 'QR Code récupéré' }
            }));
          } else {
            errors.push({
              tagId,
              message: 'Impossible de générer l\'image QR'
            });
          }
        } else {
          // Générer un nouveau QR code via l'API
          const response = await axios.post(
            `${API_BASE_URL}/tags/generate_qr.php`,
            { id_tag: tagId },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data?.success) {
            const qrData = response.data.data;
            const qrImage = await generateQRCodeImage(qrData.qr_code);
            
            if (qrImage) {
              newQrImages[tagId] = qrImage;
              
              results.push({
                id_tag: tagId,
                qr_code: qrData.qr_code,
                tag_info: tagInfo?.info_tag || `Tag ${tagId}`,
                site_info: qrData.site_info || null,
                is_new: true,
                is_existing: false
              });
              
              setQrGenerationStatus(prev => ({
                ...prev,
                [tagId]: { status: 'success', message: 'Nouveau QR Code généré' }
              }));
              
              // Mettre à jour le tag localement
              setTags(prev => prev.map(tag => 
                tag.id_tag === tagId 
                  ? { ...tag, hasExistingQR: true, qr_code: qrData.qr_code }
                  : tag
              ));
            } else {
              errors.push({
                tagId,
                message: 'Impossible de générer l\'image QR'
              });
            }
          } else {
            errors.push({
              tagId,
              message: response.data?.message || 'Erreur inconnue'
            });
          }
        }
      } catch (err) {
        if (err.response?.status === 403 && handleAbonnementError(err)) {
          // L'erreur d'abonnement est déjà gérée
          setGeneratingQR(false);
          setShowQRPreview(false);
          return;
        }
        errors.push({
          tagId,
          message: err.response?.data?.message || err.message || 'Erreur réseau'
        });
      }
    }

    setQrCodes(results);
    setQrImages(newQrImages);
    setShowQRPreview(true);

    // Afficher un résumé
    if (results.length > 0) {
      const newQRCodesCount = results.filter(r => r.is_new).length;
      const existingQRCodesCount = results.filter(r => r.is_existing).length;
      setInfoMsg(`${results.length} QR Code(s) traité(s) : ${newQRCodesCount} nouveau(x), ${existingQRCodesCount} existant(s).`);
    }

    if (errors.length > 0) {
      console.error('Erreurs lors de la génération des QR Codes:', errors);
      setInfoMsg(prev => prev + ` ${errors.length} erreur(s) de génération.`);
    }

  } catch (error) {
    console.error('Erreur lors de la génération des QR Codes:', error);
    setInfoMsg('Erreur lors de la génération des QR Codes');
  } finally {
    setGeneratingQR(false);
  }
};

  // Télécharger les QR Codes en PDF. Accepte optionnellement une liste de
  // codes/images précalculée (utilisé par exportListToPDF pour exporter la
  // liste filtrée directement, sans passer par le state qrCodes/qrImages du
  // modal "Générer QR" — nécessaire car setState est asynchrone et lire le
  // state juste après un setQrCodes()/setQrImages() renverrait l'ancienne
  // valeur).
  const downloadQRAsPDF = async (codesParam = null, imagesParam = null) => {
    const codes = codesParam ?? qrCodes;
    const images = imagesParam ?? qrImages;

    if (codes.length === 0) {
      setInfoMsg("Aucun QR Code à télécharger.");
      return;
    }

    try {
      setInfoMsg("Génération du PDF en cours...");

      // Vérifier que toutes les images QR sont disponibles
      const missingQRCodes = [];
      const validQRCodes = [];

      codes.forEach(qr => {
        if (images[qr.id_tag] && images[qr.id_tag].startsWith('data:image/')) {
          validQRCodes.push(qr);
        } else {
          missingQRCodes.push(qr.tag_info);
        }
      });

      if (missingQRCodes.length > 0) {
        console.warn("QR Codes manquants:", missingQRCodes);
        setInfoMsg(`Impossible de générer le PDF: ${missingQRCodes.length} QR Code(s) manquant(s)`);
        return;
      }

      // Créer le document PDF
      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const margin = 15;
      const qrSize = 50;
      const qrPerRow = 3;
      const qrSpacing = 25;
      const qrWidth = (pageWidth - 2 * margin - (qrPerRow - 1) * qrSpacing) / qrPerRow;
      
      let currentRow = 0;
      let currentCol = 0;
      let y = 25;

      // Fonction pour ajouter l'en-tête de page : logo à gauche, nom/adresse/
      // téléphone de la société alignés sur la même ligne (à droite du logo,
      // au lieu d'être en dessous), sous-titre centré sous l'ensemble, puis
      // séparateur avant le reste du contenu.
      const addPageHeader = (pageNumber) => {
        const logoW = 50;
        const logoH = 25;
        const logoTop = 8;
        let logoBottom = logoTop;

        if (logoBase64 && logoBase64.startsWith('data:image/')) {
          try {
            doc.addImage(logoBase64, 'PNG', margin, logoTop, logoW, logoH);
            logoBottom = logoTop + logoH;
          } catch (error) {
            console.warn("Erreur lors de l'ajout du logo:", error);
          }
        }

        // Nom + adresse/téléphone de la société, à droite du logo
        const companyTitle = COMPANY_INFO.name || "SecureControle";
        const infoX = margin + logoW + 5;
        let infoY = logoTop + 7;

        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont(undefined, 'bold');
        doc.text(companyTitle, infoX, infoY);
        infoY += 5;

        const infoLigne = [COMPANY_INFO.address, COMPANY_INFO.phone].filter(Boolean).join(' — ');
        if (infoLigne) {
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.setFont(undefined, 'normal');
          doc.text(infoLigne, infoX, infoY);
          doc.setTextColor(0);
          infoY += 5;
        }

        const headerBottom = Math.max(logoBottom, infoY) + 6;
        const subtitleY = headerBottom;

        // Titre centré sous l'ensemble logo + infos société
        doc.setFontSize(16);
        doc.setTextColor(0);
        doc.setFont(undefined, 'bold');
        doc.text("QR CODES DES TAGS", pageWidth / 2, subtitleY, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Date de génération à droite, sur la même ligne que le sous-titre
        const today = new Date().toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Généré le ${today}`, pageWidth - margin, subtitleY, { align: 'right' });

        let nextLineY = subtitleY + 6;

        // Numéro de page (seulement si plus d'une page)
        if (pageNumber > 1) {
          doc.setFontSize(8);
          doc.text(`Page ${pageNumber}`, pageWidth / 2, nextLineY, { align: 'center' });
          nextLineY += 5;
        }

        // Informations du gestionnaire si disponible
        const userData = localStorage.getItem("user");
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user.nom && user.prenom) {
              doc.setFontSize(8);
              doc.setTextColor(80);
              doc.text(`Genere par: ${user.prenom} ${user.nom}`, margin, nextLineY);
              nextLineY += 5;
            }
          } catch (e) {
            console.warn("Erreur lors de la lecture des données utilisateur:", e);
          }
        }

        // Séparateur entre le bloc en-tête et le reste du contenu
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        const lineY = nextLineY + 2;
        doc.line(margin, lineY, pageWidth - margin, lineY);
        doc.setTextColor(0);

        return lineY + 8; // position Y de départ du contenu, sous le séparateur
      };

      y = addPageHeader(1);

      // Pour chaque QR Code
      for (let i = 0; i < validQRCodes.length; i++) {
        const qr = validQRCodes[i];

        // Vérifier si on doit ajouter une nouvelle page
        if (y > pageHeight - 100) {
          doc.addPage('l');
          currentRow = 0;
          currentCol = 0;

          // Ajouter l'en-tête sur la nouvelle page
          y = addPageHeader(doc.internal.getNumberOfPages());
        }

        const x = margin + currentCol * (qrWidth + qrSpacing);
        const qrXCenter = x + qrWidth / 2;

        // Ajouter l'image QR Code
        try {
          const qrImageX = qrXCenter - qrSize/2;
          const qrImageY = y;
          
          // S'assurer que l'image QR est valide
          if (images[qr.id_tag] && images[qr.id_tag].startsWith('data:image/')) {
            doc.addImage(images[qr.id_tag], 'PNG', qrImageX, qrImageY, qrSize, qrSize);
            
            // Texte "SCANNEZ-MOI"
            doc.setFontSize(9);
            doc.setTextColor(COLORS.primary);
            doc.setFont(undefined, 'bold');
            doc.text("SCANNEZ-MOI", qrXCenter, y + qrSize + 5, { align: 'center' });
            
            // Informations du tag
            doc.setFontSize(8);
            doc.setTextColor(COLORS.darkText);
            doc.setFont(undefined, 'normal');
            
            // Nom du tag
            let tagName = qr.tag_info || `Tag ${qr.id_tag}`;
            if (tagName.length > 20) {
              tagName = tagName.substring(0, 20) + '...';
            }
            doc.text(`Tag: ${tagName}`, qrXCenter, y + qrSize + 11, { align: 'center' });
            
            // Informations du site
            if (qr.site_info?.nom_site) {
              let siteName = qr.site_info.nom_site;
              if (siteName.length > 25) {
                siteName = siteName.substring(0, 25) + '...';
              }
              doc.text(`Site: ${siteName}`, qrXCenter, y + qrSize + 17, { align: 'center' });
            }
            
            if (currentCol < qrPerRow - 1) {
              doc.setDrawColor(240, 240, 240);
              doc.setLineWidth(0.1);
              const separatorX = x + qrWidth + qrSpacing/2 - 2;
              const separatorStartY = y - 5;
              const separatorEndY = y + qrSize + 20; 
              doc.line(separatorX, separatorStartY, separatorX, separatorEndY);
            }
          } else {
            doc.setFontSize(10);
            doc.setTextColor(COLORS.danger);
            doc.text("QR Code non disponible", qrXCenter, y + qrSize/2, { align: 'center' });
          }
        } catch (error) {
          console.error('Erreur lors de l\'ajout de l\'image QR:', error);
          doc.setFontSize(10);
          doc.setTextColor(COLORS.danger);
          doc.text("Erreur QR", qrXCenter, y + qrSize/2, { align: 'center' });
        }

        currentCol++;
        if (currentCol >= qrPerRow) {
          currentCol = 0;
          currentRow++;
          y += qrSize + 40; 
        }
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        const footerLineY = pageHeight - 25;
        doc.line(margin, footerLineY, pageWidth - margin, footerLineY);
        
        doc.setFontSize(8);
        doc.setTextColor(100);
        
        // Pagination au centre
        doc.text(`Page ${i} sur ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        
        // Copyright
        doc.setFontSize(7);
        doc.setTextColor(150);
        const currentYear = new Date().getFullYear();
      }

      // Sauvegarder le PDF
      const todayStr = new Date().toISOString().split('T')[0];
      const safeCompanyName = (COMPANY_INFO.name || "SecureControle").replace(/[^\w\s]/gi, '_').replace(/\s+/g, '_');
      const fileName = `QR_Codes_${safeCompanyName}_${todayStr}.pdf`;
      
      try {
        doc.save(fileName);
        setInfoMsg(`PDF généré avec succès: ${validQRCodes.length} QR Code(s) - ${fileName}`);
      } catch (saveError) {
        console.error('Erreur lors de la sauvegarde du PDF:', saveError);
        setInfoMsg('Erreur lors de la sauvegarde du fichier PDF');
        
        // Fallback: ouvrir le PDF dans un nouvel onglet
        try {
          const pdfData = doc.output('datauristring');
          window.open(pdfData);
        } catch (openError) {
          console.error('Erreur lors de l\'ouverture du PDF:', openError);
        }
      }

    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setInfoMsg(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Export direct de la liste filtrée (recherche/statut/site/client) en PDF —
  // contrairement au flux "Générer QR" (qui demande de sélectionner des tags
  // un par un puis de prévisualiser), ce bouton exporte en un clic tous les
  // QR Codes déjà générés parmi les tags actuellement affichés. Les tags QR
  // sans code encore généré sont ignorés (on ne génère pas de nouveau code
  // comme effet de bord d'un export) et signalés dans le message final.
  const exportListToPDF = async () => {
    // Même critère que le badge "Généré" affiché dans la liste (hasExistingQR) :
    // se baser sur qr_code plutôt que sur type_tag, qui n'est pas fiable ici.
    const tagsAvecQR = filteredTags.filter(t => t.qr_code && t.qr_code.trim() !== "");

    if (tagsAvecQR.length === 0) {
      setInfoMsg("Aucun tag QR avec code déjà généré dans la liste actuelle. Utilisez \"Générer QR\" d'abord.");
      return;
    }

    setExportingList(true);
    setInfoMsg("Génération du PDF en cours...");

    try {
      const results = [];
      const images = {};

      for (const tag of tagsAvecQR) {
        const qrImage = await generateQRCodeImage(tag.qr_code);
        if (qrImage) {
          images[tag.id_tag] = qrImage;
          results.push({
            id_tag: tag.id_tag,
            qr_code: tag.qr_code,
            tag_info: tag.info_tag || `Tag ${tag.id_tag}`,
            site_info: tag.site_affecte || null,
            is_new: false,
            is_existing: true
          });
        }
      }

      await downloadQRAsPDF(results, images);

      const ignores = filteredTags.length - tagsAvecQR.length;
      if (ignores > 0) {
        setInfoMsg(prev => `${prev} (${ignores} tag(s) sans QR Code ignoré(s) — utilisez "Générer QR" pour eux.)`);
      }
    } catch (error) {
      console.error('Erreur export liste PDF:', error);
      setInfoMsg(`Erreur lors de l'export: ${error.message}`);
    } finally {
      setExportingList(false);
    }
  };

  const openActivateModal = (tag) => {
    if (tag.statut_tag === "actif") {
      setInfoMsg("Ce tag est déjà actif.");
      return;
    }
    
    setSelectedTag(tag);
    setModalAction("activate");
    setModalMessage(`Voulez-vous activer le tag "${tag.info_tag}" ?`);
    setShowActivateModal(true);
  };

  const openAffectModal = (tag) => {
    if (tag.statut_tag !== "actif") {
      setInfoMsg("Vous ne pouvez affecter que des tags actifs.");
      return;
    }
    
    if (tag.site_affecte) {
      setInfoMsg("Ce tag est déjà affecté à un site.");
      return;
    }
    
    setSelectedTag(tag);
    setSelectedSiteId("");
    setModalAction("affect");
    setModalMessage(`Affecter le tag "${tag.info_tag}" à un site`);
    setShowAffectModal(true);
  };

  const openDesaffectModal = (tag) => {
    if (!tag.site_affecte) {
      setInfoMsg("Ce tag n'est affecté à aucun site.");
      return;
    }
    
    setSelectedTag(tag);
    setModalAction("desaffect");
    setModalMessage(`Désaffecter le tag "${tag.info_tag}" du site "${tag.site_affecte.nom_site}" ?`);
    setShowConfirmModal(true);
  };

  const handleActivateTag = async () => {
    if (!selectedTag || !selectedTag.id_tag) return;
    
    setUpdatingId(selectedTag.id_tag);
    resetAbonnementError();
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }
      
      const url = `${API_BASE_URL}/tags/activer_tags.php`;
      
      const response = await axios.post(url, {
        id_tag: selectedTag.id_tag
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success) {
        fetchData();
        setInfoMsg(response.data.message || "Tag activé avec succès.");
      } else {
        setInfoMsg(response.data?.message || "Erreur lors de l'activation du tag.");
      }
    } catch (err) {
      if (err.response?.status === 403 && handleAbonnementError(err)) {
        // L'erreur d'abonnement est déjà gérée
        closeAllModals();
        return;
      } else if (err.response?.data?.message) {
        setInfoMsg(err.response.data.message);
      } else if (err.response?.status === 400 && err.response?.data?.message?.includes("déjà actif")) {
        setInfoMsg("Ce tag est déjà actif.");
      } else {
        setInfoMsg("Erreur lors de l'activation du tag.");
      }
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  const handleAffectTag = async () => {
    if (!selectedTag || !selectedTag.id_tag || !selectedSiteId) {
      setInfoMsg("Veuillez sélectionner un site.");
      return;
    }

    setUpdatingId(selectedTag.id_tag);
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }

      const url = `${API_BASE_URL}/tags/affecter_tags.php`;
      
      const response = await axios.post(url, {
        id_tag: selectedTag.id_tag,
        id_site: parseInt(selectedSiteId)
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success) {
        fetchData();
        setInfoMsg(response.data.message || "Tag affecté au site avec succès.");
      } else {
        setInfoMsg(response.data?.message || "Erreur lors de l'affectation du tag.");
      }
    } catch (err) {
      if (err.response?.status === 403 && handleAbonnementError(err)) {
        // L'erreur d'abonnement est déjà gérée
        closeAllModals();
        return;
      } else if (err.response?.data?.message) {
        setInfoMsg(err.response.data.message);
      } else if (err.response?.status === 403 && err.response?.data?.message?.includes("inactif")) {
        setInfoMsg("Impossible d'affecter un tag inactif.");
      } else if (err.response?.status === 409) {
        setInfoMsg("Ce tag est déjà affecté à ce site.");
      } else {
        setInfoMsg("Erreur lors de l'affectation du tag.");
      }
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  const handleDesaffectTag = async () => {
    if (!selectedTag || !selectedTag.id_tag || !selectedTag.site_affecte?.id_site) {
      setInfoMsg("Tag non affecté à un site.");
      closeAllModals();
      return;
    }

    setUpdatingId(selectedTag.id_tag);
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }

      const url = `${API_BASE_URL}/tags/desaffecter_tags.php`;
      
      const response = await axios.post(url, {
        id_tag: selectedTag.id_tag,
        id_site: selectedTag.site_affecte.id_site
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success) {
        fetchData();
        setInfoMsg(response.data.message || "Tag désaffecté du site avec succès.");
      } else {
        setInfoMsg(response.data?.message || "Erreur lors de la désaffectation du tag.");
      }
    } catch (err) {
      if (err.response?.status === 403 && handleAbonnementError(err)) {
        // L'erreur d'abonnement est déjà gérée
        closeAllModals();
        return;
      } else if (err.response?.data?.message) {
        setInfoMsg(err.response.data.message);
      } else {
        setInfoMsg("Erreur lors de la désaffectation du tag.");
      }
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  const closeAllModals = () => {
    setShowAffectModal(false);
    setShowConfirmModal(false);
    setShowActivateModal(false);
    setShowQRModal(false);
    setSelectedTag(null);
    setSelectedSiteId("");
    setModalAction("");
    setModalMessage("");
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTags.length > 0 ? filteredTags.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = Math.max(1, Math.ceil(filteredTags.length / itemsPerPage));

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetSearch = () => {
    setSearchTerm("");
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatut("");
    setFilterSite("");
    setFilterClient("");
  };

  const hasActiveFilters = () => {
    return searchTerm || filterStatut || filterSite || filterClient;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getSitesForFilter = () => {
    const uniqueSiteIds = new Set();
    const sitesFromTags = [];
    
    tags.forEach(tag => {
      if (tag.site_affecte && tag.site_affecte.id_site && !uniqueSiteIds.has(tag.site_affecte.id_site)) {
        uniqueSiteIds.add(tag.site_affecte.id_site);
        sitesFromTags.push({
          id: tag.site_affecte.id_site,
          nom_complet: tag.site_affecte.nom_site,
          ...tag.site_affecte
        });
      }
    });
    
    return sitesFromTags;
  };

  const getClientsForFilter = () => {
    const uniqueClientIds = new Set();
    const clientsFromTags = [];

    tags.forEach(tag => {
      const client = tag.site_affecte?.client;
      if (client && client.id_client && !uniqueClientIds.has(client.id_client)) {
        uniqueClientIds.add(client.id_client);
        clientsFromTags.push(client);
      }
    });

    return clientsFromTags;
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des Tags</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tags</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-tags"></i>
                    </div>
                    Liste des tags
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les tags RFID et affectez-les aux sites
                </p>
              </div>
              
              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={resetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
               
                <button
                  className="btn fw-semibold"
                  onClick={handleCreateTag}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Nouveau Tag
                </button>

                <button
                  className="btn fw-semibold"
                  onClick={openQRModal}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.info,
                    borderColor: COLORS.info,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-qr-code me-2"></i>
                  Générer QR
                </button>

                <button
                  className="btn fw-semibold"
                  onClick={exportListToPDF}
                  disabled={exportingList}
                  title="Exporter en PDF les QR Codes des tags actuellement affichés (déjà générés)"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.danger,
                    borderColor: COLORS.danger,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  {exportingList ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  ) : (
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                  )}
                  Exporter PDF
                </button>
              </div>
            </div>

            {/* Affichage des erreurs d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && !loading && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher par info tag..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                          {searchTerm && (
                            <button
                              className="btn"
                              type="button"
                              onClick={resetSearch}
                              title="Réinitialiser la recherche"
                              style={{
                                color: COLORS.primary,
                                borderColor: '#e0e0e0',
                                backgroundColor: COLORS.white,
                                borderLeft: 'none'
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les statuts</option>
                            <option value="actif">Actif</option>
                            <option value="inactif">Inactif</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-buildings"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterSite}
                            onChange={(e) => setFilterSite(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les sites</option>
                            {getSitesForFilter().map((site) => (
                              <option key={site.id} value={site.id}>
                                {site.nom_complet}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person-vcard"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterClient}
                            onChange={(e) => setFilterClient(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les clients</option>
                            {getClientsForFilter().map((client) => (
                              <option key={client.id_client} value={client.id_client}>
                                {client.nom_complet}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredTags.length} tag(s)
                          {filteredTags.length !== tags.length && tags.length > 0 && 
                            ` sur ${tags.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {searchTerm && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                            {filterStatut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Statut</span>}
                            {filterSite && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Site</span>}
                            {filterClient && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Client</span>}
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={fetchData}
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          fontSize: '0.85rem'
                        }}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Actualiser
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des tags...</p>
              </div>
            ) : tags.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-tags fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun tag trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucun tag n'a été enregistré pour le moment.
                </p>
                <button
                  onClick={handleCreateTag}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Créer un tag
                </button>
              </div>
            ) : (
              <>
                {filteredTags.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun tag ne correspond aux critères de recherche.
                    </p>
                    <button
                      className="btn fw-semibold mt-3"
                      onClick={resetFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                    >
                      <i className="bi bi-x-lg me-2"></i>
                      Effacer les filtres
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Info Tag</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Site Affecté</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>QR Code</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((tag, index) => {
                            const isActive = tag.statut_tag === "actif";
                            const isAffected = tag.site_affecte !== null;
                            const isUpdating = updatingId === tag.id_tag;
                            const hasQR = tag.hasExistingQR;

                            return (
                              <tr key={tag.id_tag || `row-${index}`} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className={`${isActive ? 'text-white' : 'text-white'} rounded-circle d-flex align-items-center justify-content-center fw-semibold`}
                                        style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          fontSize: '14px',
                                          backgroundColor: isActive ? COLORS.primary : '#B0BEC5',
                                          boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                        }}
                                        title="Tag"
                                      >
                                        <i className="bi bi-tag"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                        {tag.info_tag || "Sans nom"}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        ID: {tag.id_tag}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                      width: '36px',
                                      height: '36px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.primary,
                                      fontSize: '0.9rem'
                                    }}>
                                      <i className="bi bi-calendar"></i>
                                    </div>
                                    <div>
                                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                        {formatDateTime(tag.created_at).split(' ')[0]}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        {formatDateTime(tag.created_at).split(' ')[1]}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                    color: isActive ? '#2e7d32' : '#c62828',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {isActive ? (
                                      <>
                                        <i className="bi bi-check-circle me-1"></i>
                                        Actif
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-x-circle me-1"></i>
                                        Inactif
                                      </>
                                    )}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  {isAffected ? (
                                    <div className="d-flex align-items-center">
                                      <div className="me-3">
                                        <div 
                                          className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                          style={{ 
                                            width: '36px', 
                                            height: '36px', 
                                            fontSize: '13px',
                                            backgroundColor: COLORS.secondary,
                                            boxShadow: '0 2px 4px rgba(30, 136, 200, 0.1)'
                                          }}
                                          title="Site"
                                        >
                                          <i className="bi bi-building"></i>
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                          {tag.site_affecte.nom_site}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                          {tag.site_affecte.addresse_site || "Pas d'adresse"}
                                        </small>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      <i className="bi bi-building-slash me-1"></i>
                                      Non affecté
                                    </span>
                                  )}
                                </td>

                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: hasQR ? '#e8f5e9' : '#fff3e0',
                                    color: hasQR ? '#2e7d32' : '#ff9800',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {hasQR ? (
                                      <>
                                        <i className="bi bi-check-circle me-1"></i>
                                        Généré
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-dash-circle me-1"></i>
                                        Non généré
                                      </>
                                    )}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    <button
                                      className="btn btn-outline-success fw-semibold"
                                      onClick={() => handleViewDetails(tag)}
                                      title="Voir les détails"
                                      disabled={isUpdating}
                                      style={{
                                        color: COLORS.primary,
                                        borderColor: COLORS.primary,
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        minWidth: '44px'
                                      }}
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                    
                                    {!isActive ? (
                                      <button
                                        className="btn fw-semibold"
                                        onClick={() => openActivateModal(tag)}
                                        disabled={isUpdating}
                                        title="Activer le tag"
                                        style={{
                                          color: COLORS.success,
                                          borderColor: COLORS.success,
                                          backgroundColor: 'transparent',
                                          borderRadius: '6px',
                                          padding: '8px 16px',
                                          fontSize: '0.85rem',
                                          minWidth: '44px'
                                        }}
                                      >
                                        {isUpdating ? (
                                          <span className="spinner-border spinner-border-sm" role="status"></span>
                                        ) : (
                                          <i className="bi bi-power"></i>
                                        )}
                                      </button>
                                    ) : isAffected ? (
                                      <button
                                        className="btn fw-semibold"
                                        onClick={() => openDesaffectModal(tag)}
                                        disabled={isUpdating}
                                        title="Désaffecter du site"
                                        style={{
                                          color: COLORS.danger,
                                          borderColor: COLORS.danger,
                                          backgroundColor: 'transparent',
                                          borderRadius: '6px',
                                          padding: '8px 16px',
                                          fontSize: '0.85rem',
                                          minWidth: '44px'
                                        }}
                                      >
                                        {isUpdating ? (
                                          <span className="spinner-border spinner-border-sm" role="status"></span>
                                        ) : (
                                          <i className="bi bi-building-x"></i>
                                        )}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn fw-semibold"
                                        onClick={() => openAffectModal(tag)}
                                        disabled={isUpdating}
                                        title="Affecter à un site"
                                        style={{
                                          color: COLORS.info,
                                          borderColor: COLORS.info,
                                          backgroundColor: 'transparent',
                                          borderRadius: '6px',
                                          padding: '8px 16px',
                                          fontSize: '0.85rem',
                                          minWidth: '44px'
                                        }}
                                      >
                                        <i className="bi bi-building-add"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredTags.length} tag(s)
                              {filteredTags.length !== tags.length && tags.length > 0 ? ' (filtrés)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px'
                                          }}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px'
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Modal pour générer les QR Codes */}
      {showQRModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ backgroundColor: '#E3F2FD', borderBottom: `2px solid ${COLORS.accent}` }}>
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: COLORS.info,
                      color: 'white'
                    }}>
                      <i className="bi bi-qr-code"></i>
                    </div>
                    {showQRPreview ? 'QR Codes Générés' : 'Générer des QR Codes'}
                  </div>
                </h5>
                <button type="button" className="btn-close" onClick={closeQRModal} disabled={generatingQR}></button>
              </div>
              
              <div className="modal-body" style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
                {showQRPreview ? (
                  <div>
                    <div className="alert alert-success mb-4" style={{ backgroundColor: '#e8f5e9', borderColor: COLORS.success }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-check-circle me-2"></i>
                        <div>
                          <strong>{qrCodes.length} QR Code(s) traité(s) avec succès</strong>
                          <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                            {qrCodes.filter(q => q.is_new).length} nouveau(x), {qrCodes.filter(q => q.is_existing).length} existant(s)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      {qrCodes.map((qr, index) => (
                        <div key={index} className="col-md-6 mb-4">
                          <div className="card h-100" style={{ border: `1px solid ${COLORS.lightBg}`, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <div className="card-body">
                              <div className="row">
                                <div className="col-md-8">
                                  <h6 className="card-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                                    <i className="bi bi-tag me-2"></i>
                                    {qr.tag_info}
                                  </h6>
                                  
                                  <div className="mb-2">
                                    <small className="text-muted d-block">Statut QR:</small>
                                    <span className={`badge ${qr.is_new ? 'bg-success' : 'bg-info'}`}>
                                      <i className={`bi ${qr.is_new ? 'bi-plus-circle' : 'bi-check-circle'} me-1`}></i>
                                      {qr.is_new ? 'Nouveau' : 'Existant'}
                                    </span>
                                  </div>
                                  
                                  {qr.site_info && (
                                    <div className="mb-2">
                                      <small className="text-muted d-block">Site:</small>
                                      <strong>{qr.site_info.nom_site || qr.site_info.site_name}</strong>
                                    </div>
                                  )}
                                  
                                </div>
                                
                                <div className="col-md-4 text-center">
                                  {qrImages[qr.id_tag] && (
                                    <div className="text-center">
                                      <div className="mb-2">
                                        <img 
                                          src={qrImages[qr.id_tag]} 
                                          alt="QR Code" 
                                          style={{ 
                                            width: '100px', 
                                            height: '100px',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            padding: '8px',
                                            backgroundColor: 'white',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                          }}
                                        />
                                      </div>
                                      <div className="badge bg-primary" style={{ fontSize: '0.7rem' }}>
                                        SCANNEZ-MOI
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={() => {
                          setShowQRPreview(false);
                          setQrCodes([]);
                          setQrImages({});
                        }}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 24px'
                        }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Retour à la sélection
                      </button>
                      
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={downloadQRAsPDF}
                        style={{
                          color: COLORS.white,
                          backgroundColor: COLORS.danger,
                          borderColor: COLORS.danger,
                          borderRadius: '8px',
                          padding: '10px 24px'
                        }}
                      >
                        <i className="bi bi-file-pdf me-2"></i>
                        Télécharger PDF
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4">
                      <p className="mb-2">Sélectionnez les tags pour générer des QR Codes :</p>
                      <div className="text-muted small">
                        <i className="bi bi-info-circle me-1"></i>
                        Les tags avec QR Code existant seront réutilisés
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm fw-semibold"
                          onClick={selectAllTagsForQR}
                          style={{
                            color: COLORS.success,
                            borderColor: COLORS.success,
                            backgroundColor: 'transparent',
                            borderRadius: '6px',
                            padding: '6px 12px'
                          }}
                        >
                          <i className="bi bi-check-square me-1"></i>
                          Tout sélectionner
                        </button>
                        
                        <button
                          type="button"
                          className="btn btn-sm fw-semibold ms-2"
                          onClick={deselectAllTagsForQR}
                          style={{
                            color: COLORS.danger,
                            borderColor: COLORS.danger,
                            backgroundColor: 'transparent',
                            borderRadius: '6px',
                            padding: '6px 12px'
                          }}
                        >
                          <i className="bi bi-square me-1"></i>
                          Tout désélectionner
                        </button>
                      </div>
                      
                      <div className="text-muted">
                        <span className="badge" style={{ backgroundColor: COLORS.primary, color: COLORS.white }}>
                          {selectedTagsForQR.length} sélectionné(s)
                        </span>
                      </div>
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table className="table table-hover">
                        <thead>
                          <tr style={{ backgroundColor: '#F5F7FA', position: 'sticky', top: 0 }}>
                            <th style={{ width: '50px', color: COLORS.primary }}>#</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600' }}>Tag</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600' }}>Site</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600' }}>QR Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAffectedTags().map((tag, index) => {
                            const isSelected = selectedTagsForQR.includes(tag.id_tag);
                            
                            return (
                              <tr 
                                key={tag.id_tag} 
                                onClick={() => toggleTagForQR(tag.id_tag)}
                                style={{ cursor: 'pointer', backgroundColor: isSelected ? '#E3F2FD' : 'transparent' }}
                              >
                                <td style={{ verticalAlign: 'middle' }}>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isSelected}
                                      onChange={() => toggleTagForQR(tag.id_tag)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-2">
                                      <i className="bi bi-tag" style={{ color: COLORS.secondary }}></i>
                                    </div>
                                    <div>
                                      <strong>{tag.info_tag}</strong>
                                      <div className="small text-muted">
                                        ID: {tag.id_tag}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-2">
                                      <i className="bi bi-building" style={{ color: COLORS.primary }}></i>
                                    </div>
                                    <div>
                                      <strong>{tag.site_affecte.nom_site}</strong>
                                      <div className="small text-muted">
                                        {tag.site_affecte.addresse_site || 'Pas d\'adresse'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  {tag.hasExistingQR ? (
                                    <span className="badge bg-success" style={{ fontSize: '0.8rem' }}>
                                      <i className="bi bi-check-circle me-1"></i>
                                      Existant
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning" style={{ fontSize: '0.8rem' }}>
                                      <i className="bi bi-plus-circle me-1"></i>
                                      À générer
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-footer" style={{ backgroundColor: '#E3F2FD', borderTop: `1px solid #dee2e6` }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeQRModal}
                  disabled={generatingQR}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  Fermer
                </button>
                
                {!showQRPreview && (
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={generateQRCodes}
                    disabled={selectedTagsForQR.length === 0 || generatingQR}
                    style={{
                      color: COLORS.white,
                      backgroundColor: selectedTagsForQR.length === 0 ? '#B0BEC5' : COLORS.info,
                      borderColor: selectedTagsForQR.length === 0 ? '#B0BEC5' : COLORS.info,
                      borderRadius: '8px',
                      padding: '10px 24px'
                    }}
                  >
                    {generatingQR ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Génération...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-qr-code me-2"></i>
                        Générer ({selectedTagsForQR.length})
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour affecter un tag */}
      {showAffectModal && selectedTag && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ backgroundColor: '#E3F2FD', borderBottom: `2px solid ${COLORS.accent}` }}>
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: COLORS.info,
                      color: 'white'
                    }}>
                      <i className="bi bi-building-add"></i>
                    </div>
                    Affecter un tag
                  </div>
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals} disabled={updatingId === selectedTag.id_tag}></button>
              </div>
              
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="mb-4">
                  <p style={{ fontSize: '0.95rem', color: COLORS.darkText }}>
                    Vous êtes sur le point d'affecter le tag <strong>"{selectedTag.info_tag}"</strong> à un site.
                  </p>
                  
                  <div className="mb-3">
                    <label className="form-label" style={{ color: COLORS.primary, fontWeight: '500' }}>
                      <i className="bi bi-building me-2"></i>
                      Sélectionner un site
                    </label>
                    <select 
                      className="form-select"
                      value={selectedSiteId}
                      onChange={(e) => setSelectedSiteId(e.target.value)}
                      style={{
                        borderColor: '#e0e0e0',
                        color: COLORS.darkText,
                        padding: '10px'
                      }}
                    >
                      <option value="">Choisir un site...</option>
                      {sites.map((site) => (
                        <option key={site.id_site} value={site.id_site}>
                          {site.nom_site} - {site.addresse_site || 'Pas d\'adresse'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer" style={{ backgroundColor: '#E3F2FD', borderTop: `1px solid #dee2e6` }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeAllModals}
                  disabled={updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  Annuler
                </button>
                
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={handleAffectTag}
                  disabled={!selectedSiteId || updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.white,
                    backgroundColor: selectedSiteId ? COLORS.info : '#B0BEC5',
                    borderColor: selectedSiteId ? COLORS.info : '#B0BEC5',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  {updatingId === selectedTag.id_tag ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Confirmer l'affectation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation (pour désaffecter) */}
      {showConfirmModal && selectedTag && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ 
                backgroundColor: modalAction === 'desaffect' ? '#FFEBEE' : '#E3F2FD',
                borderBottom: `2px solid ${modalAction === 'desaffect' ? COLORS.danger : COLORS.accent}`,
                borderRadius: '12px 12px 0 0',
                padding: '20px'
              }}>
                <h5 className="modal-title" style={{ 
                  color: modalAction === 'desaffect' ? COLORS.danger : COLORS.primary,
                  fontWeight: '600'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: modalAction === 'desaffect' ? COLORS.danger : COLORS.warning,
                      color: 'white'
                    }}>
                      <i className={modalAction === 'desaffect' ? 'bi bi-exclamation-triangle' : 'bi bi-info-circle'}></i>
                    </div>
                    Confirmation
                  </div>
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals} disabled={updatingId === selectedTag.id_tag}></button>
              </div>
              
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto" 
                      style={{ 
                        width: '60px',
                        height: '60px',
                        backgroundColor: modalAction === 'desaffect' ? '#FFEBEE' : '#FFF3E0',
                        color: modalAction === 'desaffect' ? COLORS.danger : COLORS.warning
                      }}
                    >
                      <i className="bi bi-question-circle fs-2"></i>
                    </div>
                  </div>
                  <h6 style={{ color: COLORS.darkText, fontWeight: '600', marginBottom: '12px' }}>
                    {modalMessage}
                  </h6>
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Cette action est {modalAction === 'desaffect' ? 'irréversible' : 'importante'}.
                  </p>
                </div>
              </div>
              
              <div className="modal-footer" style={{ 
                backgroundColor: modalAction === 'desaffect' ? '#FFEBEE' : '#E3F2FD',
                borderTop: `1px solid #dee2e6`,
                borderRadius: '0 0 12px 12px',
                padding: '20px'
              }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeAllModals}
                  disabled={updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  Annuler
                </button>
                
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={modalAction === 'desaffect' ? handleDesaffectTag : handleActivateTag}
                  disabled={updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.white,
                    backgroundColor: modalAction === 'desaffect' ? COLORS.danger : COLORS.success,
                    borderColor: modalAction === 'desaffect' ? COLORS.danger : COLORS.success,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  {updatingId === selectedTag.id_tag ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Confirmer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'activation */}
      {showActivateModal && selectedTag && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ 
                backgroundColor: '#E8F5E9',
                borderBottom: `2px solid ${COLORS.success}`,
                borderRadius: '12px 12px 0 0',
                padding: '20px'
              }}>
                <h5 className="modal-title" style={{ color: '#2e7d32', fontWeight: '600' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: COLORS.success,
                      color: 'white'
                    }}>
                      <i className="bi bi-power"></i>
                    </div>
                    Activer un tag
                  </div>
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals} disabled={updatingId === selectedTag.id_tag}></button>
              </div>
              
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto" 
                      style={{ 
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#E8F5E9',
                        color: '#2e7d32'
                      }}
                    >
                      <i className="bi bi-toggle-on fs-2"></i>
                    </div>
                  </div>
                  <h6 style={{ color: COLORS.darkText, fontWeight: '600', marginBottom: '12px' }}>
                    {modalMessage}
                  </h6>
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Le tag deviendra utilisable pour être affecté à un site.
                  </p>
                </div>
              </div>
              
              <div className="modal-footer" style={{ 
                backgroundColor: '#E8F5E9',
                borderTop: `1px solid #dee2e6`,
                borderRadius: '0 0 12px 12px',
                padding: '20px'
              }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeAllModals}
                  disabled={updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  Annuler
                </button>
                
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={handleActivateTag}
                  disabled={updatingId === selectedTag.id_tag}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.success,
                    borderColor: COLORS.success,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  {updatingId === selectedTag.id_tag ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Activation...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Activer le tag
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .bi-arrow-clockwise.spin {
          animation: spin 1s linear infinite;
        }
        
        .tooltip {
          font-family: 'Roboto', sans-serif;
          font-size: 0.85rem;
        }
        
        .tooltip-inner {
          background-color: ${COLORS.primary};
          color: white;
          border-radius: 6px;
          padding: 6px 12px;
          max-width: 300px;
        }
        
        .tooltip-arrow {
          display: none;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
          
          .modal-dialog {
            margin: 16px;
            width: calc(100% - 32px);
          }
          
          .modal-lg {
            max-width: 95%;
          }
        }
        
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        .table-hover tbody tr {
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .table-hover tbody tr:hover {
          background-color: #f0f7ff;
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Layout>
  );
};

export default TagList;